import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  acceptLegal,
  fetchLegalStatus,
  openLegalDocument,
} from "@/api/legal.js";

/** Si en esta sesión ya dijo "más tarde" al protocolo, no se le vuelve a pedir. */
const POSTPONED = "legal-postponed";

/**
 * Al entrar, pide aceptar lo que cambió de versión (tarjeta #4): el aviso de
 * privacidad y el contrato bloquean —no se sigue sin aceptarlos—, el protocolo
 * se puede dejar para después. Al personal del despacho el servidor no le pide
 * nada, así que para ellos esto nunca se abre.
 */
const LegalUpdatesGate = () => {
  const [pending, setPending] = useState(null);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchLegalStatus()
      .then((data) => {
        if (!cancelled) setPending(data.pending);
      })
      .catch(() => {
        // Sin respuesta no se bloquea la app; el servidor sigue protegiendo
        // lo que exige aceptación (la carga de la e.firma).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!pending) return null;

  const blocking = pending.blocking ?? [];
  const postponed = sessionStorage.getItem(POSTPONED) === "1";
  const postponable = postponed ? [] : (pending.postponable ?? []);
  const documents = [...blocking, ...postponable];
  if (documents.length === 0) return null;

  const blockingReady = blocking.every((doc) => checked[doc.document]);

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      const chosen = documents
        .filter((doc) => checked[doc.document])
        .map((doc) => doc.document);
      const status = await acceptLegal(chosen);
      if (chosen.length < documents.length)
        sessionStorage.setItem(POSTPONED, "1");
      setPending(status.pending);
    } catch {
      setError("No se pudo guardar tu aceptación. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const later = () => {
    sessionStorage.setItem(POSTPONED, "1");
    setPending({ ...pending, postponable: [] });
  };

  return (
    <Modal show backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title as="h5">Actualizamos nuestros documentos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          {blocking.length > 0
            ? "Para seguir usando CFD-IPUNTO® necesitas aceptar la versión vigente:"
            : "Hay una versión nueva que puedes revisar ahora o más tarde:"}
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {documents.map((doc) => (
          <Form.Check
            key={doc.document}
            id={`gate-${doc.document}`}
            className="mb-2"
            checked={!!checked[doc.document]}
            onChange={(e) =>
              setChecked((prev) => ({
                ...prev,
                [doc.document]: e.target.checked,
              }))
            }
            label={
              <>
                He leído y acepto el{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openLegalDocument(doc.document);
                  }}
                >
                  {doc.title}
                </a>{" "}
                de CFD-IPUNTO® (versión {doc.version}).
              </>
            }
          />
        ))}
      </Modal.Body>
      <Modal.Footer>
        {blocking.length > 0 ? (
          <Link to="/logout" className="btn btn-link me-auto">
            Cerrar sesión
          </Link>
        ) : (
          <Button variant="link" className="me-auto" onClick={later}>
            Más tarde
          </Button>
        )}
        <Button
          variant="primary"
          onClick={accept}
          disabled={
            saving ||
            !blockingReady ||
            !documents.some((d) => checked[d.document])
          }
        >
          {saving ? "Guardando…" : "Aceptar y continuar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LegalUpdatesGate;
